import { useCallback, useMemo, useState } from "react";

import { ModalStatus, type UseModalState } from "./useModalState";

/**
 * Drop-in replacement for `useModalState` that keeps the returned object and the
 * `openModal`/`closeModal` references stable across renders, so callers can safely use them
 * in `useCallback`, `useMemo`, and `useEffect` dependency arrays.
 */
export function useStableModalState(initialModalStatus = ModalStatus.CLOSED): UseModalState {
  const [modalStatus, setModalStatus] = useState(initialModalStatus);

  const openModal = useCallback(() => {
    setModalStatus(ModalStatus.OPEN);
  }, []);

  const closeModal = useCallback(() => {
    setModalStatus(ModalStatus.CLOSED);
  }, []);

  return useMemo(
    () => ({
      modalStatus,
      setModalStatus,
      modalIsClosed: modalStatus === ModalStatus.CLOSED,
      modalIsOpen: modalStatus === ModalStatus.OPEN,
      openModal,
      closeModal,
    }),
    [modalStatus, openModal, closeModal]
  );
}
